import { isNil } from 'lodash-es';
import { MdOutlineArrowDownward, MdOutlineArrowUpward } from 'react-icons/md';
import { ConditionalRender } from '@ui';
import { useClinicalCaseContext } from '@features/contexts/ClinicalCaseContext';
import { yesNo } from '@/utils/helpers';

type ClinicalHeaderProps = {
  parameter: 'Lactate' | 'PF Ratio';
  measurementUnits?: string;
  prevValue?: number;
  currentValue?: number;
  diffLabel?: string;
  ocsTime?: string;
  timeString?: string;
  bileValue?: boolean;
};

export const ClinicalHeader = ({
  parameter,
  measurementUnits,
  prevValue = 0,
  currentValue = 0,
  diffLabel,
  ocsTime,
  timeString = '',
  bileValue,
}: ClinicalHeaderProps) => {
  const { isScs } = useClinicalCaseContext();
  const getDiffText = () => {
    if (!prevValue || prevValue === currentValue) {
      return `${parameter} ${measurementUnits}`;
    }
    const diff = Math.abs(prevValue - currentValue);
    return (
      <div className="flex items-center gap-1">
        <span>{parameter} is</span>
        {prevValue < currentValue ? (
          <>
            <span>up</span>
            <MdOutlineArrowUpward aria-label="value increased" />
          </>
        ) : (
          <>
            <span>down</span>
            <MdOutlineArrowDownward aria-label="value decreased" />
          </>
        )}
        <span className="text-m-sb">{diff.toFixed(2)}</span>
        <span>{diffLabel}</span>
      </div>
    );
  };

  return (
    <div className="flex justify-between flex-wrap items-center">
      <div className="flex flex-row text-m-r gap-2">
        {getDiffText()}
        {!isNil(bileValue) && (
          <>
            <span className="text-base-40">|</span>
            <div className="flex flex-row items-center">
              <div className="text-s-r">Bile:</div>
              <div className="bg-green-90 rounded text-s-sb py-0.5 px-2">{yesNo(bileValue)} </div>
            </div>
          </>
        )}
      </div>
      <div>
        <span className="text-l-sb">{currentValue}</span> at{' '}
        <span className="text-l-sb">
          {isScs ? (
            timeString
          ) : (
            <>
              {ocsTime} PT
              <ConditionalRender show={!!timeString.length}>, {timeString}</ConditionalRender>
            </>
          )}
        </span>
      </div>
    </div>
  );
};
